import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@algoan/dashboard/store/store';
import {
  formatBankStatements,
  formatCashFlowCategoriesFilters,
  formatCashFlowTypeFilters,
} from './bank-statements-formatters';
import {
  AccountWithIndex,
  Transaction,
  AccountType,
  TransactionsByCashFlowCategory,
  TransactionsByCashFlowType,
  BankStatement,
  TransactionCategoryFilters,
  TransactionTypeFilters,
  AccountItem,
} from '@algoan/dashboard/model';
import {
  filterBankStatements,
  getTransactionIdByCashFlowCategory,
  getTransactionIdByCashFlowType,
} from './bank-statements-calculators';
import {
  statementsFiltersSelector,
  transactionsSelector,
  analysisBoundsSelector,
  allAccountsWithIndexSelector,
  accountsSelector,
  checkingAndSavingsAccountsSelector,
} from '@algoan/dashboard/store/selectors';
import { calculateBankHistory } from '@algoan/dashboard/calculators';
import {
  filterOutLoanAccounts,
  filterOutIgnoredAccounts,
  filterOutComputedAccounts,
  extractTimestampFromObjectId,
  filterOutUndefined,
} from '@algoan/dashboard/utils';
import { accountColors, theme } from '@algoan/dashboard/theme';

export const sortedAllAccountsSelector = createSelector(
  transactionsSelector,
  allAccountsWithIndexSelector,
  (transactions: Transaction[], allAccounts: AccountWithIndex[]): AccountItem[] => {
    const transactionsOfAccount = allAccounts.map(
      (account) => transactions.filter((transaction) => transaction.accountId === account.id).length,
    );

    const accountsWithTransactionNumber = allAccounts.map((account) => {
      return { ...account, numberOfTransactions: transactionsOfAccount[allAccounts.indexOf(account)] };
    });

    const accountsById = Object.fromEntries(accountsWithTransactionNumber.map((account) => [account.id, account]));
    const sortedAccounts: AccountItem[] = [];

    accountsWithTransactionNumber.forEach((account) => {
      if (account.type === AccountType.CHECKING && accountsById[account.id]) {
        sortedAccounts.push(account);
        delete accountsById[account.id];
        if (account.relatedAccountIds) {
          const relatedAccountIds = [...new Set(account.relatedAccountIds)];

          relatedAccountIds.forEach((childId) => {
            if (accountsById[childId]) {
              sortedAccounts.push(accountsById[childId]);
              delete accountsById[childId];
            }
          });
        }
      }
    });

    Array.prototype.push.apply(sortedAccounts, Object.values(accountsById));

    if (sortedAccounts.length === 0) {
      return [];
    }

    const firstAccount = sortedAccounts[0];

    const computeColorIndex = (): number => {
      if (!firstAccount.aggregator?.id) {
        return 0;
      }
      const time = extractTimestampFromObjectId(firstAccount.aggregator.id);
      return time % accountColors.length;
    };

    const firstColorIndex = computeColorIndex();

    return sortedAccounts.map((account, index) => {
      const getColor = (): string => {
        if (index + 1 > accountColors.length) {
          return theme.extraColors.sweetGrey;
        }

        const colorIndex =
          firstColorIndex + index < accountColors.length
            ? firstColorIndex + index
            : firstColorIndex + index - accountColors.length;

        return accountColors[colorIndex];
      };

      const color = getColor();
      return {
        ...account,
        color,
      };
    });
  },
);

export const sortedAccountsSelector = createSelector(
  sortedAllAccountsSelector,
  (sortedAllAccounts: AccountItem[]): AccountItem[] => {
    return filterOutIgnoredAccounts(sortedAllAccounts);
  },
);

export const sortedAccountsWithTransactionsSelector = createSelector(
  sortedAccountsSelector,
  transactionsSelector,
  (accounts: AccountItem[], transactions: Transaction[]): AccountItem[] | undefined =>
    accounts?.filter((account) => transactions?.some((transaction) => transaction.accountId === account.id)),
);

export const bankStatementsWithLoanAccountsSelector = createSelector(
  sortedAccountsWithTransactionsSelector,
  transactionsSelector,
  analysisBoundsSelector,
  (accounts, transactions, analysisBounds): BankStatement[] | undefined => {
    return Array.isArray(accounts) && Array.isArray(transactions) && accounts.length > 0 && transactions.length > 0
      ? formatBankStatements(accounts, transactions, analysisBounds)
      : undefined;
  },
);

export const bankStatementsSelector = createSelector(
  sortedAccountsWithTransactionsSelector,
  transactionsSelector,
  analysisBoundsSelector,
  (accounts, transactions, analysisBounds): BankStatement[] | undefined => {
    if (!Array.isArray(accounts) || !Array.isArray(analysisBounds)) return undefined;
    const accountsWithoutLoanType = filterOutLoanAccounts(accounts);
    const transactionsWithoutLoanAccounts = transactions.filter((transaction: Transaction) =>
      accountsWithoutLoanType?.some((account) => account.id === transaction.accountId),
    );

    return accountsWithoutLoanType.length > 0 && transactionsWithoutLoanAccounts.length > 0
      ? formatBankStatements(accountsWithoutLoanType, transactionsWithoutLoanAccounts, analysisBounds)
      : undefined;
  },
);

export const transactionIdByCashFlowCategorySelector = (state: RootState): TransactionsByCashFlowCategory => {
  const transactions = transactionsSelector(state);

  return Array.isArray(transactions) ? getTransactionIdByCashFlowCategory(transactions) : {};
};

export const transactionIdByCashFlowTypeSelector = (state: RootState): TransactionsByCashFlowType => {
  const transactions = transactionsSelector(state);

  return Array.isArray(transactions) ? getTransactionIdByCashFlowType(transactions) : {};
};

export const filteredBankStatementsSelector = createSelector(
  (state: RootState) => bankStatementsSelector(state),
  (state: RootState) => statementsFiltersSelector(state),
  (state: RootState) => transactionIdByCashFlowCategorySelector(state),
  (state: RootState) => transactionIdByCashFlowTypeSelector(state),
  (statements, filters, transactionsByCashFlow, transactionsByCashFlowType): BankStatement[] | undefined => {
    return Array.isArray(statements) && transactionsByCashFlow !== undefined
      ? filterBankStatements(statements, transactionsByCashFlow, transactionsByCashFlowType, filters)
      : undefined;
  },
);

export const transactionCategoryFiltersListSelector = (state: RootState): TransactionCategoryFilters | undefined => {
  const transactions = transactionsSelector(state);

  return Array.isArray(transactions) ? formatCashFlowCategoriesFilters(transactions) : undefined;
};

export const transactionTypeFiltersListSelector = (state: RootState): TransactionTypeFilters | undefined => {
  const transactions = transactionsSelector(state);

  return Array.isArray(transactions) ? formatCashFlowTypeFilters(transactions) : undefined;
};

export const bankStatementsHistorySelector = createSelector(
  (state: RootState) => statementsFiltersSelector(state).accounts,
  transactionsSelector,
  accountsSelector,
  checkingAndSavingsAccountsSelector,
  analysisBoundsSelector,
  (filteredAccountIds, transactions, accounts, checkingsAccounts, analysisBounds) => {
    if (Array.isArray(accounts) && Array.isArray(checkingsAccounts) && Array.isArray(transactions)) {
      const filteredAccounts = filterOutUndefined(
        filteredAccountIds.map((accountId) => accounts.find((account) => account.id === accountId)),
      );

      const selectedAccounts = filteredAccounts.length > 0 ? filteredAccounts : checkingsAccounts;

      return calculateBankHistory(transactions, selectedAccounts, analysisBounds);
    } else {
      return undefined;
    }
  },
);

export const sortedIgnoredAccountsSelector = createSelector(
  sortedAllAccountsSelector,
  (sortedAllAccounts: AccountItem[]): AccountItem[] => {
    return filterOutComputedAccounts(sortedAllAccounts);
  },
);

export const sortedAccountsWithoutLoanTypeSelector = createSelector(
  sortedAccountsSelector,
  (sortedAccounts: AccountItem[]): AccountItem[] => {
    return filterOutLoanAccounts(sortedAccounts);
  },
);
